// Черновик к задаче: три режима в одной панели, панель — в правой колонке.
//
// **Почему три, а не один.** Три прототипа жили под `?scratch=A|B|C` с 3
// сентября 2026, и 3 сентября же было записано, что живут все три: они
// отвечают на разные вопросы. «От руки» — чертёж и прикидка, ноль
// зависимостей; «столбик» — арифметика, где важна ровная разрядность и где
// мышь бесполезна; «доска» — когда нужно двигать уже нарисованное. Решение 9
// сентября «в продукт едет доска» было прочитано как выбор ОДНОГО варианта,
// и с двумя другими ушла сама панель: доска в колонку не помещается, а больше
// в колонке ничего не осталось. Восстановлено 16 сентября 2026.
//
// **Почему доска только во весь экран, а два других — и в колонке.** Своя
// панель инструментов Excalidraw в 340 пикселей не помещается физически —
// измерено ещё на прототипе. Это ограничение доски, а не черновика: тетрадка
// и столбик — наш код, их лист сворачивается в окно (см. scratch/Sheet.tsx).
//
// **Почему лениво.** Excalidraw весит 2,5 МБ в сборке и ~1,1 МБ по сети при
// открытии. В начальный бандл ему нельзя: страницу открывают каждый день, а
// доску — нет. Свой чанк грузится при первом выборе режима и дальше лежит в
// кеше браузера. Тетрадка и столбик не стоят ничего: это два файла без
// зависимостей, и они доступны сразу.
//
// **Почему шрифты свои и почему в консоли шумно.** Excalidraw берёт шрифты с
// esm.sh ВСЕГДА, независимо от того, что лежит у нас, — около двухсот запросов
// на открытие. Убирает третий источник с пути ученика только запрет, поэтому в
// index.html стоит CSP `font-src 'self' https://fonts.gstatic.com`, а пять
// семейств с проверенными лицензиями лежат в `public/excalidraw/`. Сторожит это
// `tests/fonts.test.ts` — режим отказа тут молчащий.
//
// **Два места, одна панель.** С 24 сентября 2026 черновик есть и сам по себе,
// своим экраном (`variant="page"`, src/screens/Scratchpad.tsx). Там панель
// занимает экран целиком, поэтому заголовка и пояснения над ней нет вовсе:
// «назад» уезжает внутрь её шапки, а три режима встают в ту же строку. Иначе
// над листом, ради которого пришли, стояло бы четыре строки оформления.
//
// **Ничто не размонтируется.** Ни смена режима, ни сворачивание: размонтировать
// значит стереть написанное, а это ровно та ошибка, из-за которой к инструменту
// перестают возвращаться. Поэтому развёрнутый вид — не отдельный компонент, а
// другой набор классов на том же узле, а режимы прячутся через `display`.

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Formula } from "../lib/Formula";
import { emptyScratchDraft, type ScratchDraft } from "../lib/task-draft";
import { Notebook } from "./scratch/Notebook";
import { Column } from "./scratch/Column";
import { ScratchIcon } from "./scratch/ScratchIcon";

const Excalidraw = lazy(async () => {
  const [mod] = await Promise.all([
    import("@excalidraw/excalidraw"),
    import("@excalidraw/excalidraw/index.css"),
  ]);
  return { default: mod.Excalidraw };
});

// Константа, а не литерал в JSX: Excalidraw сверяет props на каждый свой
// апдейт, и всё, что создаётся заново в рендере, лишний раз его перерисовывает.
const BOARD_UI_OPTIONS = {
  canvasActions: {
    // Всё, что уводит из задачи или тянет файлы: сохранение сцены, экспорт,
    // картинка, чужая тема. Черновик это черновик — он не файл и не хранится.
    changeViewBackgroundColor: false,
    loadScene: false,
    saveToActiveFile: false,
    export: false,
    saveAsImage: false,
    toggleTheme: false,
  },
} as const;

type Mode = ScratchDraft["mode"];

const MODES: [Mode, string][] = [
  ["hand", "от руки"],
  ["column", "столбик"],
  ["board", "доска"],
];

/** Тема доски следует теме приложения: переключатель стоит в шапке рядом. */
function useAppTheme(): "light" | "dark" {
  const [theme, setTheme] = useState<"light" | "dark">("light");
  useEffect(() => {
    const read = (): void =>
      setTheme(document.documentElement.dataset.theme === "dark" ? "dark" : "light");
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return theme;
}

export function Scratch({
  statement,
  variant = "task",
  draft: storedDraft,
  onChange: storeDraft,
}: {
  /**
   * Условие задачи в шапке развёрнутого вида. Необязательно: на отдельном
   * экране «Черновики» (src/screens/Scratchpad.tsx) задачи нет вовсе, и
   * пустая строка там — не «условие потерялось», а его отсутствие.
   */
  statement?: string;
  /**
   * `task` — карточка в правой колонке экрана задачи. `page` — панель во всю
   * ширину на своём экране (src/screens/Scratchpad.tsx).
   *
   * Отличаются они одной строкой, и она стоит того. На своём экране шапка
   * панели идёт во всю ширину и почти пуста, а под ней отдельной строкой
   * висят три режима — то есть две строки оформления над листом, ради
   * которого сюда и пришли. В `page` режимы встают в ту же шапку. В колонке
   * задачи так нельзя: 340 пикселей заняты подписью и разворотом.
   */
  variant?: "task" | "page";
  draft?: ScratchDraft;
  onChange?: (draft: ScratchDraft) => void;
}) {
  const [localDraft, setLocalDraft] = useState(emptyScratchDraft);
  const draft = storedDraft ?? localDraft;
  const onChange = storeDraft ?? setLocalDraft;
  const [mode, setMode] = useState<Mode>(draft.mode);
  const [expanded, setExpanded] = useState(false);
  const [boardMounted, setBoardMounted] = useState(draft.board !== null);
  const root = useRef<HTMLDivElement | null>(null);
  const expandButton = useRef<HTMLButtonElement | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // Обработчик держится в ссылке, а не в зависимостях. Excalidraw сравнивает
  // props поверхностно и перерисовывается от НОВОЙ функции, а перерисовка
  // безусловно зовёт onChange (componentDidUpdate) — то есть нестабильный
  // обработчик замыкает петлю «onChange → состояние → новый проп → onChange»
  // и роняет экран на «Maximum update depth exceeded». Так `change` и все
  // производные от него обработчики стабильны навсегда, даже если вызывающий
  // передаст стрелку прямо в JSX.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const theme = useAppTheme();

  const change = useCallback(
    (patch: Partial<ScratchDraft>): void => onChangeRef.current({ ...draftRef.current, ...patch }),
    [],
  );

  useEffect(() => {
    if (!expanded) return;
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const onKey = (event: KeyboardEvent): void => {
      // Escape внутри ввода принадлежит вводу: им отменяют набранную подпись
      // на листе и на доске, и свернуть черновик заодно — потеря написанного.
      const el = event.target as HTMLElement | null;
      if (
        event.key === "Escape" &&
        !(el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.isContentEditable)
      ) {
        setExpanded(false);
        return;
      }
      if (event.key !== "Tab" || root.current === null) return;
      const focusable = [...root.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((node) => node.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    const frame = window.requestAnimationFrame(() => expandButton.current?.focus());
    // Страница под оверлеем не должна прокручиваться колесом.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      previousFocus?.focus({ preventScroll: true });
    };
  }, [expanded]);

  const choose = (next: Mode): void => {
    setMode(next);
    change({ mode: next });
    if (next === "board") {
      setBoardMounted(true);
      // Доска осмыслена только развёрнутой: своя панель инструментов в колонку
      // не помещается физически.
      setExpanded(true);
    }
  };

  const notebookChanged = useCallback(
    (notebook: ScratchDraft["notebook"]) => change({ notebook }),
    [change],
  );
  const columnChanged = useCallback(
    (column: NonNullable<ScratchDraft["column"]>) => change({ column }),
    [change],
  );
  const boardChanged = useCallback(
    (elements: NonNullable<ScratchDraft["board"]>["elements"]) =>
      change({ board: { elements } }),
    [change],
  );
  const boardShown = mode === "board" && expanded;

  // Режимы — один и тот же ряд в обоих местах: в шапке на своём экране, под
  // шапкой в колонке задачи. Разметка одна, чтобы не разъехалась на первой же
  // правке.
  const modes = (
    <div className={`chips scratch__modes${variant === "page" && !expanded ? " scratch__modes--head" : ""}`}>
      {MODES.map(([key, label]) => (
        <button
          key={key}
          className="chip scratch__icon-button"
          aria-label={label}
          title={label}
          aria-pressed={mode === key}
          onClick={() => choose(key)}
        >
          <ScratchIcon name={key} />
        </button>
      ))}
    </div>
  );
  const inHead = variant === "page" && !expanded;

  const expand = (
    <button
      ref={expandButton}
      className="chip scratch__icon-button"
      aria-label={expanded ? "Свернуть черновик" : "Открыть черновик во весь экран"}
      title={expanded ? "Свернуть" : "Во весь экран"}
      onClick={() => setExpanded((v) => !v)}
    >
      <ScratchIcon name={expanded ? "collapse" : "expand"} />
    </button>
  );

  return (
    <div
      ref={root}
      className={
        expanded
          ? "scratch scratch--full"
          : `card scratch scratch--inline scratch--${variant}`
      }
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded ? true : undefined}
      aria-label={
        expanded ? (variant === "page" ? "Черновик" : "Черновик к задаче") : undefined
      }
    >
      <div className="scratch__head">
        {/* «Назад» здесь нет: выход с экрана — шевроном в шапке приложения,
            одинаково на всех экранах (src/ui/Shell.tsx). */}
        <span className="card__label">Черновик</span>
        {/*
          Условие в шапке развёрнутого вида — не украшение: черновик занимает
          экран целиком, а считать по задаче, которой не видно, нельзя. За этим
          и уходят в тетрадку.
        */}
        {expanded && statement !== undefined && statement !== "" ? (
          <div className="scratch__statement">
            <Formula source={statement} />
          </div>
        ) : (
          <span style={{ flex: 1 }} />
        )}
        {/* Режимы и разворот — одним куском: на узком экране кнопки не влезают
            в строку с подписью, и переноситься они должны вместе. Поодиночке
            разворот уезжал на свою строку и вставал под «назад» слева. */}
        {inHead ? <div className="scratch__head-tools">{modes}{expand}</div> : expand}
      </div>

      {inHead ? null : modes}

      <div className="scratch__pane" style={{ display: mode === "hand" ? "flex" : "none" }}>
        <Notebook
          visible={mode === "hand"}
          expanded={expanded}
          initialShapes={draft.notebook}
          onChange={notebookChanged}
        />
      </div>
      <div className="scratch__pane" style={{ display: mode === "column" ? "flex" : "none" }}>
        <Column
          visible={mode === "column"}
          expanded={expanded}
          initialDraft={draft.column}
          onChange={columnChanged}
        />
      </div>

      {/* Свёрнутая доска не прячется вместе с панелью, а объясняет себя: иначе
          «доска» в свёрнутом виде выглядит как режим, который сломался. */}
      {mode === "board" && !expanded ? (
        <div className="scratch__note">
          Доска — только во весь экран: её панель инструментов в колонку не
          помещается. Нарисованное на месте.{" "}
          <button className="link" onClick={() => setExpanded(true)}>
            развернуть
          </button>
        </div>
      ) : null}

      {boardMounted ? (
        <div className="scratch__pane" style={{ display: boardShown ? "flex" : "none" }}>
          <div className="scratch__board">
            <Suspense
              fallback={
                <div style={{ padding: 20 }}>
                  <div className="skeleton" style={{ width: 220 }} />
                </div>
              }
            >
              <Excalidraw
                theme={theme}
                langCode="ru-RU"
                initialData={{
                  elements: (draft.board?.elements ?? []) as never[],
                  appState: {
                    // Скетчевый контур и рукописный шрифт — против «строгого
                    // инструмента». Гасятся по одной настройке каждый.
                    currentItemRoughness: 0,
                    // Liberation Sans (9). Было 2 — «Helvetica», а это
                    // УСТАРЕВШЕЕ значение: современные семейства нумерованы
                    // 5–9. Liberation взят по двум причинам сразу: он самый
                    // простой из пяти, то есть ближе всех к тону, и его
                    // лицензия проверена у первоисточника (SIL OFL 1.1).
                    currentItemFontFamily: 9,
                    gridSize: 20,
                    gridModeEnabled: true,
                  },
                }}
                onChange={boardChanged}
                UIOptions={BOARD_UI_OPTIONS}
              />
            </Suspense>
          </div>
        </div>
      ) : null}
    </div>
  );
}
